import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PhygitalIcon, { PoweredByPhygital } from "@/components/PhygitalIcon";
import {
  Code,
  Zap,
  Shield,
  Webhook,
  Key,
  Copy,
  Check,
  ChevronRight,
  Terminal,
  Sparkles,
  Users,
  Calendar,
  QrCode,
  ArrowRight,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const DeveloperPage = () => {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState(null);
  const [activeTab, setActiveTab] = useState("quickstart");

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock = ({ code, language = "javascript", id }) => (
    <div className="relative group">
      <pre className="bg-gray-950 text-gray-100 p-4 rounded-xl text-sm overflow-x-auto font-mono">
        <code>{code}</code>
      </pre>
      <button
        onClick={() => copyCode(code, id)}
        className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copiedCode === id ? (
          <Check className="w-4 h-4 text-green-400" />
        ) : (
          <Copy className="w-4 h-4 text-gray-500" />
        )}
      </button>
    </div>
  );

  const pricingTiers = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      features: [
        "100 API calls/month",
        "Basic endpoints",
        "Community support",
        "Phygital branding required"
      ],
      cta: "Get Started",
      highlighted: false
    },
    {
      name: "Pro",
      price: "$44",
      period: "/month",
      features: [
        "5,000 API calls/month",
        "Webhooks",
        "Remove branding option",
        "Email support"
      ],
      cta: "Start Pro",
      highlighted: true
    },
    {
      name: "Scale",
      price: "$111",
      period: "/month",
      features: [
        "25,000 API calls/month",
        "Priority support",
        "Custom fields",
        "SLA guarantee"
      ],
      cta: "Start Scale",
      highlighted: false
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      features: [
        "Unlimited API calls",
        "White-glove onboarding",
        "Custom SLA",
        "Direct Slack channel"
      ],
      cta: "Contact Us",
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-100 sticky top-0 bg-white/80 backdrop-blur-lg z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <PhygitalIcon className="w-8 h-8" showBox={true} />
            <span className="font-semibold text-lg">phygital</span>
            <span className="text-gray-300">|</span>
            <span className="text-gray-700 text-sm font-medium">Developers</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="#quickstart" className="text-sm text-gray-700 hover:text-gray-900">Quick Start</a>
            <a href="#api" className="text-sm text-gray-700 hover:text-gray-900">API Reference</a>
            <a href="#pricing" className="text-sm text-gray-700 hover:text-gray-900">Pricing</a>
            <Button size="sm" className="bg-gray-900 hover:bg-gray-800 text-white">
              Get API Key
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Preference API for IRL Experiences
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Build personalized experiences<br />with one integration
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Access user preferences like accessibility, dietary, and communication style to 
            create experiences that feel personal from the moment they arrive.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white px-8">
              Get Your API Key
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="px-8">
              View Documentation
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-12">Who uses the Phygital API?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Calendar,
                title: "Event Platforms",
                desc: "Add preference collection to your RSVP flow. Know your guests before they arrive.",
                examples: "Eventbrite, Partiful, Posh"
              },
              {
                icon: Users,
                title: "Venues and Hotels",
                desc: "Check-in kiosk integration. Staff sees guest needs instantly upon arrival.",
                examples: "Hotels, conference centers, clubs"
              },
              {
                icon: QrCode,
                title: "Booking Systems",
                desc: "Pull client preferences before appointments. No more intake forms.",
                examples: "Salons, spas, service providers"
              }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-gray-700" />
                </div>
                <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-700 text-sm mb-3">{item.desc}</p>
                <p className="text-xs text-gray-500">{item.examples}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Start */}
      <section id="quickstart" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">Quick Start</h2>
          <p className="text-gray-700 mb-8">Get up and running in 5 minutes.</p>

          {/* Step 1 */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-medium">1</div>
              <h3 className="font-semibold text-lg">Get your API key</h3>
            </div>
            <p className="text-gray-700 mb-4 ml-11">
              Sign up for a free account and generate your API key from the dashboard.
            </p>
            <div className="ml-11">
              <CodeBlock 
                id="apikey"
                code={`# Your API key will look like this:
PHYGITAL_API_KEY=phy_live_abc123xyz789`}
              />
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-medium">2</div>
              <h3 className="font-semibold text-lg">Create an event</h3>
            </div>
            <div className="ml-11">
              <CodeBlock 
                id="create-event"
                code={`const response = await fetch('https://api.phygital.com/v1/events', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer phy_live_abc123xyz789',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    name: 'High Tech High Fashion',
    slug: 'hthf-lafw-2026',
    date: '2026-03-11',
    venue_name: 'Downtown LA',
    collect_preferences: ['accessibility', 'dietary', 'vibe', 'interests']
  })
});

const event = await response.json();
console.log(event.event_id); // evt_abc123`}
              />
            </div>
          </div>

          {/* Step 3 */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-medium">3</div>
              <h3 className="font-semibold text-lg">Get guest preferences at check-in</h3>
            </div>
            <div className="ml-11">
              <CodeBlock 
                id="get-prefs"
                code={`// When guest scans their QR code or NFC pass
const passToken = 'PHY-ABC123XYZ';

const response = await fetch(
  \`https://api.phygital.com/v1/events/\${eventId}/checkin/\${passToken}\`,
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer phy_live_abc123xyz789'
    }
  }
);

const guest = await response.json();

// What your staff sees:
// {
//   guest_name: "Jordan Smith",
//   preferences: {
//     accessibility: ["wheelchair_access", "hearing_loop"],
//     dietary: ["vegan", "nut_allergy"],
//     vibe: "ready_to_mingle",
//     invisible_disabilities: ["chronic_fatigue"]
//   }
// }`}
              />
            </div>
          </div>
        </div>
      </section>

      {/* API Reference */}
      <section id="api" className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4">API Reference</h2>
          <p className="text-gray-700 mb-8">Core endpoints for the Phygital API.</p>

          <div className="space-y-4">
            {[
              { method: "POST", path: "/v1/events", desc: "Create a new event" },
              { method: "GET", path: "/v1/events/{id}", desc: "Get event details" },
              { method: "POST", path: "/v1/events/{id}/rsvp", desc: "RSVP a guest to an event" },
              { method: "GET", path: "/v1/events/{id}/guests", desc: "List all guests with preferences" },
              { method: "POST", path: "/v1/events/{id}/preferences", desc: "Save guest preferences" },
              { method: "GET", path: "/v1/users/{id}/preferences", desc: "Get a user's universal preferences" },
              { method: "POST", path: "/v1/passes/generate", desc: "Generate a digital pass" },
              { method: "GET", path: "/v1/passes/{token}", desc: "Validate and get pass details" },
              { method: "POST", path: "/v1/passes/{token}/checkin", desc: "Check in a guest" },
              { method: "POST", path: "/v1/webhooks", desc: "Register a webhook" }
            ].map((endpoint, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-200 flex items-center gap-4">
                <span className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                  endpoint.method === 'GET' ? 'bg-green-100 text-green-700' :
                  endpoint.method === 'POST' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {endpoint.method}
                </span>
                <code className="text-sm font-mono text-gray-700 flex-1">{endpoint.path}</code>
                <span className="text-sm text-gray-600">{endpoint.desc}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button variant="outline" className="gap-2">
              View Full Documentation
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Webhooks */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Webhook className="w-8 h-8 text-purple-600" />
            <h2 className="text-3xl font-bold">Webhooks</h2>
          </div>
          <p className="text-gray-700 mb-8">Get real-time notifications when things happen.</p>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              { event: "guest.rsvp", desc: "When a guest RSVPs to your event" },
              { event: "guest.checkin", desc: "When a guest checks in at your venue" },
              { event: "preferences.updated", desc: "When a guest updates their preferences" },
              { event: "pass.generated", desc: "When a digital pass is created" }
            ].map((webhook, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <code className="text-sm font-mono text-purple-600 font-medium">{webhook.event}</code>
                <p className="text-sm text-gray-700 mt-1">{webhook.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-700">Start free, scale as you grow.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            {pricingTiers.map((tier, i) => (
              <div 
                key={i}
                className={`rounded-2xl p-6 ${
                  tier.highlighted 
                    ? 'bg-gray-900 text-white border-2 border-gray-900' 
                    : 'bg-white border border-gray-200'
                }`}
              >
                <h3 className={`font-semibold text-lg mb-1 ${tier.highlighted ? 'text-white' : 'text-gray-900'}`}>
                  {tier.name}
                </h3>
                <div className="mb-4">
                  <span className={`text-3xl font-bold ${tier.highlighted ? 'text-white' : 'text-gray-900'}`}>
                    {tier.price}
                  </span>
                  <span className={`text-sm ${tier.highlighted ? 'text-gray-500' : 'text-gray-600'}`}>
                    {tier.period}
                  </span>
                </div>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, j) => (
                    <li key={j} className={`text-sm flex items-start gap-2 ${tier.highlighted ? 'text-gray-300' : 'text-gray-700'}`}>
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.highlighted ? 'text-green-400' : 'text-green-600'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full ${
                    tier.highlighted 
                      ? 'bg-white text-gray-900 hover:bg-gray-100' 
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                  size="sm"
                >
                  {tier.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to build?</h2>
          <p className="text-gray-700 mb-8">
            Get your API key and start creating personalized experiences in minutes.
          </p>
          <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white px-8">
            Get Started Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <PoweredByPhygital />
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <a href="#" className="hover:text-gray-900">Documentation</a>
            <a href="#" className="hover:text-gray-900">Status</a>
            <a href="#" className="hover:text-gray-900">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DeveloperPage;
